import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  Ban,
  Check,
  CheckCheck,
  Circle,
  CircleDashed,
  CircleDot,
  Clock,
  Eye,
  HelpCircle,
  Link2,
  Pause,
  PlayCircle,
  RotateCw,
  X,
} from "lucide-react";
import type {
  Project,
  WorkItem,
  WorkPriority,
  WorkflowDefinition,
} from "@captain-tasko/shared";
import { STATUS_TO_STAGE, WORK_PRIORITIES } from "@captain-tasko/shared";
import { apiFetch, apiList, apiSingle } from "../lib/api.js";
import { SkeletonRows } from "../components/Skeleton.js";
import { EmptyState } from "../components/EmptyState.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { useRealtime } from "../lib/realtime.js";
import { toast } from "../components/Toast.js";
import { PriorityPill } from "../components/PriorityPill.js";
import { RowActionsMenu } from "../components/RowActions.js";
import { downloadCsv } from "../lib/csv.js";
import { SortableTh } from "../components/SortableTh.js";
import { naturalKey, useTableSort } from "../lib/sort.js";
import { UserChips } from "../components/UserPicker.js";
import { copyToClipboard } from "../lib/clipboard.js";
import { Pagination } from "../components/Pagination.js";
import { NestFilterChip } from "../components/NestFilterChip.js";
import {
  ColumnsMenu,
  useColPrefs,
  type ColumnDef,
} from "../components/ColumnsMenu.js";
import {
  daysPastDue,
  formatDaysPastDueShort,
  formatWorkItemKey,
} from "../lib/format.js";
import { useSession } from "../hooks/useSession.js";

// ----- Column config: persisted to localStorage -----
// Extracted to apps/web/src/components/ColumnsMenu.tsx in ctsk-061 so
// DocumentLibrary can reuse the same pattern. The COLUMNS list stays
// page-specific (these are the work-item-only columns).

// Column widths tuned 2026-05-22: Title was unbounded → ate enough
// space to wrap the date/time, assignee, and label columns. Second
// pass tightened the front (Key was 88 but mostly empty space) and
// the compact right-side cells (Due, Updated, etc.) so the front
// stops feeling heavy and there's less air between Labels and Due.
// Reference look-and-feel is the Projects table (uses base `.grid`
// with no inter-column borders).
const COLUMNS: ColumnDef[] = [
  // CTSK-237 — watch/star column. Leftmost by design (before Key) so
  // the watching state reads as a per-row "pin" the way Linear/GitHub
  // render theirs. Icon-only: no header label, 32px tight. Sortable —
  // desc bubbles watched rows to the top for one-click triage focus;
  // the DEFAULT sort stays updatedAt desc (AC#3: opt-in sort).
  {
    key: "watch",
    label: "",
    width: 32,
    defaultVisible: true,
    sortable: true,
  },
  { key: "key", label: "Key", width: 44, defaultVisible: true, sortable: true },
  {
    key: "title",
    label: "Title",
    width: 320,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    width: 92,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "priority",
    label: "Priority",
    width: 76,
    defaultVisible: true,
    sortable: true,
  },
  // Docs column — chain icon when the work item has at least one row
  // in document_work_item_links. Sortable on linkedDocumentCount so
  // operators can pull "items with the most context" or "items missing
  // any spec" to the top of the page in a click.
  {
    key: "docs",
    label: "Docs",
    width: 44,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "assignees",
    label: "Assignees",
    width: 120,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "labels",
    label: "Labels",
    // 110 → 88 (−20%) per dr.the0 — Labels was overweighted vs. its content.
    width: 88,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "dueDate",
    label: "Due",
    width: 88,
    defaultVisible: true,
    sortable: true,
  },
  {
    key: "createdAt",
    label: "Created",
    width: 88,
    defaultVisible: false,
    sortable: true,
  },
  {
    key: "updatedAt",
    label: "Updated",
    width: 116,
    defaultVisible: true,
    sortable: true,
  },
];

// Compact columns get tighter L/R padding (6px vs the .grid base
// 14px) so date / badge / pill cells hug their content. The visual
// result: Key/Status/Priority/Due/Updated columns shed ~16px of
// dead space each, removing the wide gap between Labels and Due.
// Title and the chip columns keep the base padding for breathing
// room around variable-width content.
const COMPACT_KEYS = new Set([
  "watch",
  "key",
  "status",
  "priority",
  "docs",
  "dueDate",
  "createdAt",
  "updatedAt",
]);

// ----- Status display (0.1.6) -----
// Each canonical work-item status gets an icon + a pretty label.
// Mirrors `DEFAULT_WORK_STATUSES` (packages/shared/src/constants.ts)
// and the colors in `apps/api/src/workflow/default-workflow.ts` so
// the status column scans by glyph + hue at a glance, not just text.
// `STATUS_ICON` is a render-time function so callers don't have to
// remember the size; sized 12 to match the badge text height.
function statusIcon(status: string): React.ReactNode {
  const SIZE = 12;
  switch (status) {
    case "backlog":
      return <Circle size={SIZE} aria-hidden="true" />;
    case "spec_needed":
      return <HelpCircle size={SIZE} aria-hidden="true" />;
    case "todo":
      return <CircleDashed size={SIZE} aria-hidden="true" />;
    case "ready":
      return <CircleDot size={SIZE} aria-hidden="true" />;
    case "waiting_on_decision":
      return <Pause size={SIZE} aria-hidden="true" />;
    case "in_progress":
      return <PlayCircle size={SIZE} aria-hidden="true" />;
    case "in_review":
      return <Eye size={SIZE} aria-hidden="true" />;
    case "needs_iteration":
      return <RotateCw size={SIZE} aria-hidden="true" />;
    case "blocked":
      return <Ban size={SIZE} aria-hidden="true" />;
    case "done":
      return <Check size={SIZE} aria-hidden="true" />;
    case "verified":
      return <CheckCheck size={SIZE} aria-hidden="true" />;
    case "cancelled":
      return <X size={SIZE} aria-hidden="true" />;
    case "deferred":
      return <Clock size={SIZE} aria-hidden="true" />;
    default:
      return null;
  }
}

// Pretty labels — `in_progress` reads better as "In progress".
const STATUS_LABEL: Record<string, string> = {
  backlog: "Backlog",
  spec_needed: "Spec needed",
  todo: "Todo",
  ready: "Ready",
  waiting_on_decision: "Waiting",
  in_progress: "In progress",
  in_review: "In review",
  needs_iteration: "Needs iter.",
  blocked: "Blocked",
  done: "Done",
  verified: "Verified",
  cancelled: "Cancelled",
  deferred: "Deferred",
};

// Every column except Title is single-line / nowrap. Title gets
// overflow:hidden so the cell stays inside its 320-wide track and
// the title string truncates with ellipsis instead of wrapping.
//
// Key↔Title seam (0.1.6 follow-up): the Key cell drops its RIGHT
// padding entirely and the Title cell drops its LEFT padding to 4
// — the identifier and the title now sit ~4px apart, reading as
// one visual unit instead of two columns with a gulf between them.
// The accent color on the key alone supplies the hierarchy that
// the earlier 6+6=12px gap was trying to do with whitespace.
//
// Row density (post-0.1.6): vertical padding tightened 10px → 7px
// across all cell types to reduce the airy gap between rows.
// Title uses a full four-value shorthand so its top/bottom don't
// fall back to the CSS base (10px) while only paddingLeft is set.
function tdStyleFor(key: string): React.CSSProperties {
  if (key === "title") {
    // Full shorthand: top=7 right=14 bottom=7 left=4
    return { overflow: "hidden", maxWidth: 320, padding: "7px 14px 7px 4px" };
  }
  if (key === "key") {
    // 6px left so the key clears the table border; 0 right so it
    // sits flush against the Title cell.
    return {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      padding: "7px 0 7px 6px",
    };
  }
  if (COMPACT_KEYS.has(key)) {
    return {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      padding: "7px 6px",
    };
  }
  // assignees, labels — keep base horizontal padding, tighten vertical
  return {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "7px 14px",
  };
}

function thStyleFor(key: string, width?: number): React.CSSProperties {
  // Title gets minWidth/maxWidth bounds so it doesn't dominate the
  // proportional slack distribution under auto layout. The inner
  // div still applies its own maxWidth: 320 + overflow hidden so
  // long titles truncate cleanly even if the cell grows wider.
  const base: React.CSSProperties = {
    width,
    minWidth: key === "title" ? 200 : undefined,
    maxWidth: key === "title" ? width : undefined,
    whiteSpace: "nowrap",
    // Vertical padding matches td (7px) for a uniform row height.
    paddingTop: 7,
    paddingBottom: 7,
  };
  if (COMPACT_KEYS.has(key)) {
    base.padding = "7px 6px";
  }
  if (key === "title") {
    base.paddingLeft = 4;
    // paddingTop / paddingBottom already set in base above.
  }
  if (key === "key") {
    // Match td: no right padding so the header reads in line with
    // the cells below it.
    base.padding = "7px 0 7px 6px";
  }
  return base;
}

const COL_STORE_KEY = "tasko.workitems.cols.v1";

// ----- Pagination: persisted page-size pref -----
//
// Page itself doesn't survive reloads (the user expects a fresh "page 1"
// after coming back) — but page-size is a workflow choice and should stick.
// Cap at MAX_PAGE_LIMIT (500) to match the API; offer a sensible ladder.

const PAGE_SIZES = [25, 50, 100, 200, 500] as const;
type PageSize = (typeof PAGE_SIZES)[number];
const PAGE_SIZE_STORE_KEY = "tasko.workitems.pageSize.v1";

function loadPageSize(): PageSize {
  if (typeof window === "undefined") return 25;
  const raw = window.localStorage.getItem(PAGE_SIZE_STORE_KEY);
  const n = raw ? Number(raw) : NaN;
  return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : 25;
}

function savePageSize(n: PageSize) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PAGE_SIZE_STORE_KEY, String(n));
}

export function WorkItemsList({
  onNavigate,
  projectIdParam,
  assigneeParam,
  stageParam,
  flagParam,
}: {
  onNavigate: (p: string) => void;
  projectIdParam?: string;
  // Dashboard deep-link params. Each KPI card on the dashboard
  // navigates with the matching URL filter; we seed the page's filter
  // state from these on mount so the user sees the slice they
  // clicked into, not the unfiltered list.
  assigneeParam?: string;
  stageParam?: string;
  flagParam?: string;
}) {
  const qc = useQueryClient();
  const { session } = useSession();

  // ----- Filter session-state restoration (FR shipped 2026-05-23) -----
  //
  // Before this, every filter reset on each navigation back from a detail
  // page — operator complaint: "I have to set all my filters or project
  // again, this becomes annoying." Solution: mirror every filter to the
  // URL (replaceState, no history pollution) and prefer URL on mount.
  //
  // Priority order for initial state:
  //   1. URL search param (operator's last state, restored via back nav)
  //   2. Prop derived from URL via router (deep-link from dashboard KPI
  //      cards — projectIdParam, assigneeParam, stageParam, flagParam)
  //   3. Hard default ('' / [] / 'all' / false / null)
  //
  // Once the URL mirror writes, dashboard shortcut params (stage) get
  // materialized into their canonical form (status[]) and cleared from
  // the URL. From then on the canonical params are source of truth.
  const initialUrlParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;

  const [projectId, setProjectId] = useState(
    initialUrlParams?.get("projectId") ?? projectIdParam ?? "",
  );
  // Stage param → status[] seed. 'started' is the dashboard "In progress"
  // card; expand to the four status values that map to that stage in
  // STATUS_TO_STAGE (in_progress, in_review, needs_iteration, blocked).
  // Generalized so future stage params (unstarted, completed) also work
  // without another switch statement.
  const initialStatusFromStage = useMemo(() => {
    if (!stageParam) return [] as string[];
    return Object.entries(STATUS_TO_STAGE)
      .filter(([, stage]) => stage === stageParam)
      .map(([status]) => status);
  }, [stageParam]);
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const url = initialUrlParams?.get("status");
    if (url) return url.split(",").filter(Boolean);
    return initialStatusFromStage;
  });
  const [priorityFilter, setPriorityFilter] = useState<WorkPriority[]>(() => {
    const url = initialUrlParams?.get("priority");
    if (!url) return [];
    return url
      .split(",")
      .filter((p): p is WorkPriority =>
        (WORK_PRIORITIES as readonly string[]).includes(p),
      );
  });
  // Label filter (FR captured 2026-05-29). Client-side narrow over the
  // already-fetched page, mirroring the assignee/stale/flag pattern —
  // labels aren't currently a server-side `/workitems?label=` filter,
  // and the page-size cap keeps the client-side narrow honest for the
  // common case. URL-mirrored as repeated ?label=<name> entries so a
  // multi-label filter round-trips through refresh + share.
  const [labelFilter, setLabelFilter] = useState<string[]>(() => {
    const url = initialUrlParams?.getAll("label").filter(Boolean) ?? [];
    return url;
  });
  // Date range filters (FR 2026-05-29). Updated-between window. URL
  // mirrored as ?dateFrom=YYYY-MM-DD&dateTo=YYYY-MM-DD. Server-side via
  // the existing list-route filters (the API already accepts these).
  const [dateFrom, setDateFrom] = useState<string>(
    () => initialUrlParams?.get("dateFrom") ?? "",
  );
  const [dateTo, setDateTo] = useState<string>(
    () => initialUrlParams?.get("dateTo") ?? "",
  );
  // Filter panel visibility (FR 2026-05-29). When open, the panel
  // surfaces every multi-value filter that doesn't fit on the toolbar
  // (Status / Priority / Labels / Date range / Project). Mirrors the
  // DocumentLibrary "Show filters" pattern. Defaults closed — operator
  // sees the same compact toolbar they had before until they click in.
  const [showFilters, setShowFilters] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Phase quick-filters (#99). All client-side so they don't fight the
  // server-side pagination — they narrow the displayed page, not the
  // query. For a fuller experience these'd hit API params but the
  // page-size cap means client-side is fine for the common case.
  const [assigneeFilter, setAssigneeFilter] = useState<
    "all" | "mine" | "unassigned"
  >(() => {
    const url = initialUrlParams?.get("assignee");
    if (url === "mine" || url === "unassigned" || url === "all") return url;
    return assigneeParam === "mine" || assigneeParam === "unassigned"
      ? assigneeParam
      : "all";
  });
  const [staleFilter, setStaleFilter] = useState<boolean>(
    initialUrlParams?.get("stale") === "1",
  );
  // 0.1.35 — "Hide done" quick-filter toggle (dr.the0 2026-06-11).
  // Client-side narrow that drops rows with status === 'done'.
  // Mirrored to URL as `hideDone=1`.
  const [hideDone, setHideDone] = useState<boolean>(
    initialUrlParams?.get("hideDone") === "1",
  );
  // Flag filter — Dashboard deep-link from the attention strip
  // (Past due / Blocking / Needs attention). Client-side narrow over
  // the derived flags the API already stamps on each row.
  const [flagFilter, setFlagFilter] = useState<
    "overdue" | "blocking" | "attention" | null
  >(() => {
    const url = initialUrlParams?.get("flag");
    if (url === "overdue" || url === "blocking" || url === "attention")
      return url;
    if (
      flagParam === "overdue" ||
      flagParam === "blocking" ||
      flagParam === "attention"
    ) {
      return flagParam;
    }
    return null;
  });
  // #112 Watch quick filter. Server-side (unlike assignee/stale which are
  // client-side) because it changes the result *set*, not just the visible
  // slice — important for accurate pagination counts.
  const [watchFilter, setWatchFilter] = useState<"all" | "mine" | "not-mine">(
    () => {
      const url = initialUrlParams?.get("watch");
      if (url === "mine" || url === "not-mine" || url === "all") return url;
      return "all";
    },
  );
  // Linked-documents quick filter (0.1.6). Server-side via /workitems?hasDocs=
  // (EXISTS / NOT EXISTS against document_work_item_links). Same reasoning as
  // watchFilter — narrowing the result set has to happen before pagination
  // or the totals are wrong. Operator triage use case: "show me work items
  // that don't have a spec attached yet."
  const [hasDocsFilter, setHasDocsFilter] = useState<
    "all" | "with" | "without"
  >(() => {
    const url = initialUrlParams?.get("hasDocs");
    if (url === "with" || url === "without" || url === "all") return url;
    return "all";
  });
  // ctsk-056 — nest filter (server-side via the labels predicate; work
  // items carry the nest as a label like `nest/<slug>`, matching the
  // master-tag convention on documents).
  const [nestSlug, setNestSlug] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("nest");
  });
  // Free-text search. Server-side via /workitems?q= which OR-matches
  // title + externalId (the 'Align display key with external_id'
  // feature shipped alongside the externalId display rollout). Local
  // `q` state holds the input value; `debouncedQ` is what actually
  // hits the API — 300ms debounce avoids firing a request on every
  // keystroke.
  const [q, setQ] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("q") ?? "";
  });
  const [debouncedQ, setDebouncedQ] = useState<string>(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const STALE_DAYS = 14;

  // Pagination: page is local to the session (resets on reload); page size
  // is persisted (it's a workflow preference).
  const [pageSize, setPageSize] = useState<PageSize>(loadPageSize);
  const [page, setPage] = useState(1);

  // Any time the filter set changes, jump back to page 1 — otherwise the
  // user could end up on a page that's beyond the new total.
  useEffect(() => {
    setPage(1);
  }, [
    projectId,
    statusFilter,
    priorityFilter,
    pageSize,
    assigneeFilter,
    staleFilter,
    flagFilter,
    watchFilter,
    hasDocsFilter,
    nestSlug,
    debouncedQ,
    labelFilter,
    dateFrom,
    dateTo,
  ]);

  // Mirror every filter into the URL so the operator's filter state
  // survives navigation (back from detail, follow a link, refresh).
  // Originally just nest + search (ctsk-056); extended 2026-05-23 to
  // cover the full filter set after the operator flagged "I have to
  // set all my filters or project again, this becomes annoying."
  //
  // Uses replaceState so we don't fill back-button history with
  // intermediate filter tweaks — only "real" navigation (clicking
  // into a detail) gets a history entry. Dashboard shortcut param
  // `stage` is cleared once we've written the canonical `status`
  // form, so it never re-seeds out of date.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (projectId) sp.set("projectId", projectId);
    else sp.delete("projectId");
    if (statusFilter.length) sp.set("status", statusFilter.join(","));
    else sp.delete("status");
    if (priorityFilter.length) sp.set("priority", priorityFilter.join(","));
    else sp.delete("priority");
    // Labels — repeated ?label=<name> param to match the
    // /documents?tag= pattern that the runner already understands.
    sp.delete("label");
    for (const l of labelFilter) sp.append("label", l);
    if (assigneeFilter !== "all") sp.set("assignee", assigneeFilter);
    else sp.delete("assignee");
    if (staleFilter) sp.set("stale", "1");
    else sp.delete("stale");
    if (flagFilter) sp.set("flag", flagFilter);
    else sp.delete("flag");
    // 0.1.35 — hideDone (Hide done) quick-filter mirror.
    if (hideDone) sp.set("hideDone", "1");
    else sp.delete("hideDone");
    if (watchFilter !== "all") sp.set("watch", watchFilter);
    else sp.delete("watch");
    if (hasDocsFilter !== "all") sp.set("hasDocs", hasDocsFilter);
    else sp.delete("hasDocs");
    if (nestSlug) sp.set("nest", nestSlug);
    else sp.delete("nest");
    if (debouncedQ) sp.set("q", debouncedQ);
    else sp.delete("q");
    if (dateFrom) sp.set("dateFrom", dateFrom);
    else sp.delete("dateFrom");
    if (dateTo) sp.set("dateTo", dateTo);
    else sp.delete("dateTo");
    // Dashboard `stage` shortcut has been materialized into `status` —
    // drop it so a stale stage value doesn't override the operator's
    // explicit status edits on the next mount.
    sp.delete("stage");
    const qs = sp.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [
    projectId,
    statusFilter,
    priorityFilter,
    assigneeFilter,
    staleFilter,
    flagFilter,
    hideDone,
    watchFilter,
    hasDocsFilter,
    nestSlug,
    debouncedQ,
    labelFilter,
    dateFrom,
    dateTo,
  ]);

  // Persist page-size selection.
  useEffect(() => {
    savePageSize(pageSize);
  }, [pageSize]);

  const projectsQ = useQuery({
    queryKey: ["projects"],
    queryFn: () => apiList<Project>("/projects"),
  });

  // CTSK-363 Slice B — per-project default filter.
  //
  // The FR's "project detail workitems tab" isn't a literal tab in this
  // codebase — ProjectDetail.tsx is read-only and just links out to
  // /workitems?projectId=<id>, which is where the real (already
  // URL-driven, see the big comment above) filter state lives. So the
  // default hydrates HERE: when this page is opened with `projectId`
  // and no OTHER explicit query param, and that project has a saved
  // defaultFilterQuery, apply it once. An explicit/shared URL — i.e.
  // any query param beyond `projectId` — always wins (AC#4).
  const appliedDefaultFilterRef = useRef(false);
  const currentProject = projectsQ.data?.data.find((p) => p.id === projectId) ?? null;
  useEffect(() => {
    if (appliedDefaultFilterRef.current) return;
    if (typeof window === "undefined") return;
    if (!projectId) return;
    if (!currentProject) return; // wait for the projects list to load
    appliedDefaultFilterRef.current = true; // only ever attempt once per mount
    if (!currentProject.defaultFilterQuery) return;

    const sp = new URLSearchParams(window.location.search);
    const onlyProjectIdPresent =
      sp.get("projectId") === projectId && Array.from(sp.keys()).every((k) => k === "projectId");
    if (!onlyProjectIdPresent) return; // explicit params present — they win

    const defaults = new URLSearchParams(currentProject.defaultFilterQuery);
    const status = defaults.get("status");
    if (status) setStatusFilter(status.split(",").filter(Boolean));
    const priority = defaults.get("priority");
    if (priority) {
      setPriorityFilter(
        priority
          .split(",")
          .filter((p): p is WorkPriority => (WORK_PRIORITIES as readonly string[]).includes(p)),
      );
    }
    const labels = defaults.getAll("label").filter(Boolean);
    if (labels.length) setLabelFilter(labels);
    const assignee = defaults.get("assignee");
    if (assignee === "mine" || assignee === "unassigned" || assignee === "all") {
      setAssigneeFilter(assignee);
    }
    if (defaults.get("stale") === "1") setStaleFilter(true);
    if (defaults.get("hideDone") === "1") setHideDone(true);
    const flag = defaults.get("flag");
    if (flag === "overdue" || flag === "blocking" || flag === "attention") setFlagFilter(flag);
    const watch = defaults.get("watch");
    if (watch === "mine" || watch === "not-mine" || watch === "all") setWatchFilter(watch);
    const hasDocs = defaults.get("hasDocs");
    if (hasDocs === "with" || hasDocs === "without" || hasDocs === "all") setHasDocsFilter(hasDocs);
    const nest = defaults.get("nest");
    if (nest) setNestSlug(nest);
    const df = defaults.get("dateFrom");
    if (df) setDateFrom(df);
    const dt = defaults.get("dateTo");
    if (dt) setDateTo(dt);
    const qParam = defaults.get("q");
    if (qParam) setQ(qParam);
    // The existing URL-mirror effect (above) picks up these setters on
    // its next run and replaceState()s the merged query — no direct
    // history write needed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentProject]);

  // Save/clear the current filter selection as this project's default
  // (CTSK-363 Slice B AC#3/#5). Gated on workitems:edit — same
  // capability PATCH /projects/:id itself requires.
  const canEditProjectDefault = session?.capabilities?.includes("workitems:edit") ?? false;
  const saveDefaultFilter = useMutation({
    mutationFn: (query: string) =>
      apiSingle<Project>(`/projects/${projectId}`, {
        method: "PATCH",
        body: { defaultFilterQuery: query },
      }),
    onSuccess: () => {
      toast.success("Saved as this project's default filter");
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Map the segmented watch filter to the API's watchedBy param. 'all' →
  // omit (no filter); 'mine' → 'me'; 'not-mine' → 'not-me'.
  const watchedByParam =
    watchFilter === "mine"
      ? "me"
      : watchFilter === "not-mine"
        ? "not-me"
        : undefined;

  // Dashboard deep-link "Assigned to me" — push to server via the
  // existing assigneeId filter so the narrow happens BEFORE pagination.
  // Without this, the client-side narrow only narrows the visible
  // page, so a 100-of-690 deep-link looked like 4-of-25.
  const assigneeIdParam =
    assigneeFilter === "mine" && session?.user.id ? session.user.id : undefined;

  // Linked-documents quick filter → ?hasDocs= ('with' | 'without' | omit).
  const hasDocsParam = hasDocsFilter === "all" ? undefined : hasDocsFilter;

  const itemsQ = useQuery({
    queryKey: [
      "workitems",
      {
        projectId,
        statusFilter,
        priorityFilter,
        pageSize,
        page,
        watchedByParam,
        nestSlug,
        assigneeIdParam,
        flagFilter,
        hasDocsParam,
        debouncedQ,
        // CTSK-180 hotfix (2026-07-24): `hideDone` MUST be in the queryKey
        // — it's a server-side filter (passed as ?hideDone=1 below) that
        // changes the response shape (count + rows). Missing here caused
        // a regression where toggling Hide-done returned cached page data
        // with stale count until manual refresh. Same rule applies to any
        // future server-side query param — mirror it in both places.
        hideDone,
      },
    ],
    queryFn: () =>
      apiList<WorkItem>("/workitems", {
        query: {
          projectId: projectId || undefined,
          status: statusFilter.length ? statusFilter : undefined,
          priority: priorityFilter.length ? priorityFilter : undefined,
          watchedBy: watchedByParam,
          assigneeId: assigneeIdParam,
          // Dashboard deep-link narrow — server-side so pagination
          // counts the matching slice, not the whole org.
          flag: flagFilter ?? undefined,
          // 0.1.6 — linked-documents narrow. Server-side EXISTS / NOT
          // EXISTS against document_work_item_links so totals are
          // correct (a "Without docs" page wouldn't be the right size
          // if we filtered the visible rows post-pagination).
          hasDocs: hasDocsParam,
          // ctsk-056 — nest filter via the labels predicate. The /workitems
          // route already supports ?label=<name> with OR semantics; we add
          // the nest's master label so it AND-composes with status/priority.
          label: nestSlug ? [`nest/${nestSlug}`] : undefined,
          // Free-text search across title + externalId + key. Server-side
          // via the predicate in routes/work-items.ts so a page-1-of-25
          // search hit doesn't get masked by pagination.
          q: debouncedQ.trim() || undefined,
          // 0.1.51 — "Hide done" is server-side so the pagination total
          // reflects the effective filtered set. Previously the client
          // narrow only affected the current page, leaving
          // "Showing 1-100 of 822" stale.
          hideDone: hideDone ? 1 : undefined,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      }),
    placeholderData: (prev) => prev, // keep the old page visible during refetch — no flicker
  });

  const total = itemsQ.data?.page?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Guard against ending up on a now-out-of-range page after filter changes.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useRealtime({
    types: [
      "work.created",
      "work.updated",
      "work.deleted",
      "work.status_changed",
    ],
    onEvent: () => qc.invalidateQueries({ queryKey: ["workitems"] }),
  });

  const hasProjects = (projectsQ.data?.data.length ?? 0) > 0;

  // ctsk-061 — column config now lives in a shared hook + component
  // so the Documents list can adopt the same affordance. WorkItemsList
  // is the canonical consumer and was the source of the original code.
  const {
    prefs: colPrefs,
    setPrefs: setColPrefs,
    visibleCols,
  } = useColPrefs(COL_STORE_KEY, COLUMNS);

  // Apply the residual client-side narrows on top of the API result.
  // After Dashboard deep-link bug fix, the heavy filters (assignee=mine,
  // flag, status, project) are all server-side. What's left here:
  // - `unassigned` (no server-side "is-empty" predicate yet)
  // - `staleFilter` (depends on a relative-time threshold the server
  //   doesn't know about)
  // - belt-and-braces flag predicate so a stale cached page never
  //   shows mismatched rows.
  const filteredItems = useMemo(() => {
    const raw = itemsQ.data?.data ?? [];
    if (raw.length === 0) return raw;
    const staleCutoff = staleFilter ? Date.now() - STALE_DAYS * 86_400_000 : 0;

    return raw.filter((w) => {
      // Assignee filter. `mine` is server-side via assigneeId now (see
      // the apiList call above) so the narrow here would be redundant.
      // `unassigned` stays client-side because there's no server-side
      // "assigneeIds is empty" predicate yet.
      if (assigneeFilter === "unassigned") {
        if (w.assigneeIds.length > 0) return false;
      }
      // Stale filter — last activity older than threshold
      if (staleFilter) {
        const updated = new Date(w.updatedAt).getTime();
        if (Number.isNaN(updated)) return false;
        if (updated >= staleCutoff) return false;
      }
      // Flag filter is server-side now (the row count matched on the
      // dashboard but not on the page — page-1 of 25 of 690 contained
      // only a few matching rows). Leaving the predicate here as
      // belt-and-braces in case a stale page renders an item that
      // shouldn't match.
      if (flagFilter === "overdue" && !w.isPastDue) return false;
      if (flagFilter === "blocking" && !w.isBlocking) return false;
      if (flagFilter === "attention" && !w.needsAttention) return false;
      // 0.1.35 — Hide done quick-filter (dr.the0 2026-06-11). Client-
      // side narrow that drops any work item in a terminal workflow stage.
      // CTSK-180 (2026-07-24): broadened from `status === 'done'` to
      // `workflowStage in ('completed', 'cancelled')` — the two terminal
      // stages defined in WORKFLOW_STAGES. Covers done + verified
      // (completed) and cancelled + deferred (cancelled). workflowStage
      // is derived on read from STATUS_TO_STAGE server-side, so both
      // filters use the same source of truth. Belt-and-braces to the
      // server-side notInArray(TERMINAL_WORKITEM_STATUSES) predicate.
      if (hideDone && (w.workflowStage === "completed" || w.workflowStage === "cancelled")) return false;
      // Label filter — show the work item only if it carries at least
      // one of the active label values. Empty filter = no narrow.
      if (labelFilter.length > 0) {
        const labels = w.labels ?? [];
        if (!labelFilter.some((l) => labels.includes(l))) return false;
      }
      // Updated-between date range filter. Both bounds inclusive; an
      // empty bound is treated as "no constraint on that side".
      if (dateFrom) {
        const updated = new Date(w.updatedAt).getTime();
        const from = new Date(dateFrom).getTime();
        if (Number.isFinite(from) && updated < from) return false;
      }
      if (dateTo) {
        const updated = new Date(w.updatedAt).getTime();
        // dateTo is treated as end-of-day so a YYYY-MM-DD pick is inclusive.
        const to = new Date(dateTo + "T23:59:59").getTime();
        if (Number.isFinite(to) && updated > to) return false;
      }
      return true;
    });
  }, [itemsQ.data?.data, assigneeFilter, staleFilter, flagFilter, hideDone, labelFilter, dateFrom, dateTo]);

  // FR 2026-05-29 — count of active filters. Shown as a badge on the
  // "Filters" button and gates the visibility of the "Reset all" link.
  // Quick-chip filters (assignee=mine/unassigned, flag=overdue/blocking
  // /attention, stale, watch) count too — they're filters from the
  // operator's perspective even when the underlying state is just a
  // string segment value.
  const activeFilterCount =
    (projectId ? 1 : 0) +
    statusFilter.length +
    priorityFilter.length +
    labelFilter.length +
    (assigneeFilter !== "all" ? 1 : 0) +
    (flagFilter ? 1 : 0) +
    (staleFilter ? 1 : 0) +
    (hideDone ? 1 : 0) +
    (watchFilter !== "all" ? 1 : 0) +
    (hasDocsFilter !== "all" ? 1 : 0) +
    (nestSlug ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (debouncedQ ? 1 : 0);

  function resetAllFilters() {
    setProjectId("");
    setStatusFilter([]);
    setPriorityFilter([]);
    setLabelFilter([]);
    setAssigneeFilter("all");
    setFlagFilter(null);
    setStaleFilter(false);
    setHideDone(false);
    setWatchFilter("all");
    setHasDocsFilter("all");
    setNestSlug(null);
    setDateFrom("");
    setDateTo("");
    setQ("");
  }

  const { sorted, sort, setSort } = useTableSort(
    filteredItems,
    {
      // CTSK-237 — star column sort: watched=1 / not=0, so desc puts
      // watched rows first. Opt-in via the column header; the default
      // sort below stays updatedAt desc (AC#3).
      watch: (w: WorkItem) => (w.isWatchedByMe ? 1 : 0),
      // naturalKey() zero-pads the numeric suffix so CTSK-2 sorts before
      // CTSK-10 (was: lex sort put CTSK-10 between CTSK-1 and CTSK-19).
      key: (w: WorkItem) => naturalKey(w.key),
      title: (w) => w.title,
      status: (w) => w.status,
      priority: (w) => {
        // Order by importance, not alphabet
        const order = { critical: 4, high: 3, medium: 2, low: 1 } as Record<
          string,
          number
        >;
        return order[w.priority] ?? 0;
      },
      docs: (w) => w.linkedDocumentCount ?? 0,
      assignees: (w) => w.assigneeIds.length,
      labels: (w) => w.labels.length,
      dueDate: (w) => w.dueDate ?? "",
      createdAt: (w) => w.createdAt,
      updatedAt: (w) => w.updatedAt,
    },
    { key: "updatedAt", dir: "desc" },
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Work items</h1>
          <div className="page-subtitle">
            {total === 0
              ? "0 items"
              : // CTSK-461 — labelFilter is a client-side narrow over the
                // already-fetched page (see the comment above `labelFilter`'s
                // useState and the filteredItems predicate below); `total`
                // is the server-side count BEFORE that narrow. When a label
                // filter is active, the plain "Showing X–Y of total" line
                // was lying — it counted rows the label filter had just
                // hidden. Report the honest post-filter row count instead,
                // scoped to "on page" since the label narrow only sees the
                // current page, not the full result set (Option A per the
                // ticket; server-side label filtering is a fast-follow).
                labelFilter.length > 0
                ? `Filtered to ${filteredItems.length.toLocaleString()} (label match) of ${total.toLocaleString()} on page`
                : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className="btn btn--ghost"
            aria-label="Export as CSV"
            title="Export current list as CSV"
            onClick={() => {
              const rows = itemsQ.data?.data ?? [];
              downloadCsv(
                "work-items.csv",
                [
                  { header: "Key", accessor: (r: WorkItem) => r.key },
                  { header: "Title", accessor: (r) => r.title },
                  { header: "Status", accessor: (r) => r.status },
                  { header: "Priority", accessor: (r) => r.priority },
                  {
                    header: "Assignees",
                    accessor: (r) => r.assigneeIds.length,
                  },
                  { header: "Labels", accessor: (r) => r.labels },
                  { header: "Due", accessor: (r) => r.dueDate ?? "" },
                  { header: "Created", accessor: (r) => r.createdAt },
                  { header: "Updated", accessor: (r) => r.updatedAt },
                  { header: "Parent", accessor: (r) => r.parentId ?? "" },
                  { header: "Project", accessor: (r) => r.projectId },
                  { header: "ID", accessor: (r) => r.id },
                ],
                rows,
              );
            }}
          >
            ⤓ CSV
          </button>
          <button
            className="btn"
            onClick={() => onNavigate("/workitems/board")}
          >
            Board view →
          </button>
          <button
            className="btn btn--create"
            onClick={() => {
              if (!hasProjects) {
                toast.error("Create a project first.");
                onNavigate("/projects");
                return;
              }
              setShowCreate(true);
            }}
          >
            + New work item
          </button>
        </div>
      </div>

      {/* Quick filter row (#99/#112/0.1.6) — assignee + watching + stale + has-docs */}
      <QuickFilters
        assigneeFilter={assigneeFilter}
        onAssigneeChange={setAssigneeFilter}
        watchFilter={watchFilter}
        onWatchChange={setWatchFilter}
        staleFilter={staleFilter}
        onStaleChange={setStaleFilter}
        staleDays={STALE_DAYS}
        hasDocsFilter={hasDocsFilter}
        onHasDocsChange={setHasDocsFilter}
        anyFilterActive={
          assigneeFilter !== "all" ||
          watchFilter !== "all" ||
          hasDocsFilter !== "all" ||
          staleFilter ||
          flagFilter !== null ||
          statusFilter.length > 0 ||
          priorityFilter.length > 0 ||
          !!projectId ||
          !!nestSlug ||
          !!debouncedQ.trim()
        }
        onClearAll={() => {
          setAssigneeFilter("all");
          setWatchFilter("all");
          setHasDocsFilter("all");
          setStaleFilter(false);
          setFlagFilter(null);
          setStatusFilter([]);
          setPriorityFilter([]);
          setProjectId("");
          setNestSlug(null);
          setQ("");
        }}
      />

      {/* Dashboard deep-link banner — visible while a flag filter is
          applied so the user can SEE what's being filtered (and
          dismiss it without hunting through the existing chips). */}
      {flagFilter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            marginBottom: 8,
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          <span style={{ color: "var(--text-muted)" }}>
            Filtered from Dashboard:
          </span>
          <strong style={{ color: "var(--text-primary)" }}>
            {flagFilter === "overdue" && "Past due only"}
            {flagFilter === "blocking" && "Blocking only"}
            {flagFilter === "attention" && "Needs attention only"}
          </strong>
          <button
            type="button"
            onClick={() => setFlagFilter(null)}
            aria-label="Clear dashboard flag filter"
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
              fontSize: 14,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Nest filter chip row (ctsk-056). Hides itself when the org has
          no nests so non-nest orgs don't see clutter. */}
      <div style={{ marginBottom: 8 }}>
        <NestFilterChip value={nestSlug} onChange={setNestSlug} />
      </div>

      {/* Quick-filter chip row (FR 2026-05-29). Each chip is a preset
          that maps to one or more underlying filter values — the
          operator's most-common slices in one click. "All" clears just
          the quick-filter buckets (assignee + flag); explicit filters
          stay so the operator can compose. */}
      <WorkItemQuickChips
        assigneeFilter={assigneeFilter}
        flagFilter={flagFilter}
        priorityFilter={priorityFilter}
        onTogglePriority={(p) =>
          setPriorityFilter(
            priorityFilter.includes(p)
              ? priorityFilter.filter((x) => x !== p)
              : [...priorityFilter, p],
          )
        }
        hideDone={hideDone}
        onToggleHideDone={() => setHideDone((v) => !v)}
        onAll={() => { setAssigneeFilter("all"); setFlagFilter(null); }}
        onMine={() => { setAssigneeFilter("mine"); setFlagFilter(null); }}
        onUnassigned={() => { setAssigneeFilter("unassigned"); setFlagFilter(null); }}
        onOverdue={() => { setFlagFilter("overdue"); }}
        onBlocking={() => { setFlagFilter("blocking"); }}
        onAttention={() => { setFlagFilter("attention"); }}
      />

      <div className="toolbar" style={{ flexWrap: "wrap" }}>
        {/* Free-text search across title + externalId. 300ms debounce
            (see debouncedQ effect). 'q' is also mirrored to the URL so
            a search result page can be linked to. */}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title or external id…"
          aria-label="Search work items"
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid var(--border-strong)",
            minWidth: 220,
          }}
        />
        {/* FR 2026-05-29 — Filters toggle. Sits between the search
            input and the Columns menu, mirrors DocumentLibrary §4.1.
            The badge counts every active filter (incl. the quick-chip
            ones above) so the operator always knows how narrow the
            current slice is. */}
        <button
          className="btn btn--ghost"
          onClick={() => setShowFilters((v) => !v)}
        >
          {showFilters ? "Hide filters" : "Show filters"}
          {activeFilterCount > 0 && (
            <span
              style={{
                marginLeft: 6,
                padding: "0 6px",
                borderRadius: 999,
                background: "var(--accent-primary)",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button className="btn btn--ghost" onClick={resetAllFilters}>
            Reset all
          </button>
        )}
        {/* CTSK-363 Slice B — only meaningful when scoped to one
            project (a default filter is a per-project setting), and
            only for callers who could actually PATCH the project. */}
        {projectId && canEditProjectDefault && (
          <button
            className="btn btn--ghost"
            onClick={() => {
              const sp = new URLSearchParams(window.location.search);
              sp.delete("projectId");
              saveDefaultFilter.mutate(sp.toString());
            }}
            disabled={saveDefaultFilter.isPending}
            title="Save the current filter selection as this project's default (applies whenever this project's work items are opened with no other filters set)"
          >
            Save current filter as project default
          </button>
        )}
        {projectId && canEditProjectDefault && currentProject?.defaultFilterQuery && (
          <button
            className="btn btn--ghost"
            onClick={() => saveDefaultFilter.mutate("")}
            disabled={saveDefaultFilter.isPending}
            title="Clear this project's saved default filter"
          >
            Clear default
          </button>
        )}
        <ColumnsMenu
          columns={COLUMNS}
          prefs={colPrefs}
          onChange={setColPrefs}
        />
        <div style={{ flex: 1 }} />
        <Pagination
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(n) => setPageSize(n as PageSize)}
          compact
        />
      </div>

      {showFilters && (
        <WorkItemFiltersPanel
          projectId={projectId}
          onProjectIdChange={setProjectId}
          projects={projectsQ.data?.data ?? []}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          priorityFilter={priorityFilter}
          onPriorityFilterChange={setPriorityFilter}
          labelFilter={labelFilter}
          onLabelFilterChange={setLabelFilter}
          // Labels-in-view (mirrors DocumentLibrary's tags-in-view from
          // ctsk-080). Only labels actually present on the current page
          // are offered, plus any tag already selected so it can be
          // deselected even if the active filters move it off-screen.
          labelsInView={Array.from(
            new Set([
              ...(itemsQ.data?.data ?? []).flatMap((w) => w.labels ?? []),
              ...labelFilter,
            ]),
          ).sort()}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
        />
      )}

      {itemsQ.isLoading && <SkeletonRows rows={6} />}
      {itemsQ.data && itemsQ.data.data.length === 0 && (
        <EmptyState
          title={hasProjects ? "No work items match" : "Create a project first"}
          body={
            hasProjects
              ? "Try clearing filters or create a new item."
              : "Work items live under projects."
          }
          action={
            hasProjects ? (
              <button
                className="btn btn--create"
                onClick={() => setShowCreate(true)}
              >
                + New work item
              </button>
            ) : (
              <button
                className="btn btn--create"
                onClick={() => onNavigate("/projects")}
              >
                + Create a project
              </button>
            )
          }
        />
      )}
      {itemsQ.data && itemsQ.data.data.length > 0 && (
        <FlagLegend items={itemsQ.data.data} excludePastDue />
      )}
      {itemsQ.data && itemsQ.data.data.length > 0 && (
        // Auto layout. A previous pass tried table-layout: fixed to
        // pin Key at exactly 56 — but fixed layout truncated the
        // key text ("ctsk-084" → "ctsk-08") AND starved every other
        // column of width since Title was set to width: auto and
        // greedily absorbed the slack. Auto layout treats the per
        // -column widths as hints, so we trade exact-pinning for
        // every column having content room. Key↔Title gap is held
        // tight by the asymmetric padding in tdStyleFor / thStyleFor
        // (Key R-pad 0, Title L-pad 4).
        <table className="grid">
          <thead>
            <tr>
              {visibleCols.map((c) => {
                // 0.1.35 (dr.the0 2026-06-11) — Past due red dot + label
                // moved INTO the Key column header so the indicator
                // sits with the data it qualifies, not as a free-
                // floating legend above the table. Shows only when at
                // least one visible row carries isPastDue.
                const isKeyCol = c.key === 'key';
                const showPastDue = isKeyCol && itemsQ.data!.data.some((i) => i.isPastDue);
                return (
                  <SortableTh
                    key={c.key}
                    sortKey={c.key}
                    sort={sort}
                    onSortChange={setSort}
                    sortable={c.sortable}
                    style={thStyleFor(c.key, c.width)}
                  >
                    {c.label}
                    {showPastDue && (
                      <span
                        style={{
                          marginLeft: 12,
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}
                        title="At least one row on this page is past due"
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: 'var(--danger-primary)',
                            marginRight: 4,
                            verticalAlign: 'middle',
                          }}
                        />
                        Past due
                      </span>
                    )}
                  </SortableTh>
                );
              })}
              <th style={{ width: 28, padding: 0 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <WorkItemRow
                key={w.id}
                item={w}
                // ctsk-071 — pass the resolved project name so the Key
                // tooltip can read e.g. "code-tron". Falls back to the
                // raw projectId when the project list hasn't loaded yet.
                projectName={
                  projectsQ.data?.data.find((p) => p.id === w.projectId)?.name
                }
                // 0.1.51 — pass the resolved project color so the Key
                // cell renders in the project's own color instead of
                // inheriting the current org's --accent-primary. Bug 2
                // fix.
                projectColor={
                  projectsQ.data?.data.find((p) => p.id === w.projectId)?.color ?? undefined
                }
                cols={visibleCols}
                onClick={() => onNavigate(`/workitems/${w.id}`)}
                onAction={() =>
                  qc.invalidateQueries({ queryKey: ["workitems"] })
                }
                onNavigate={onNavigate}
                // FR captured 2026-05-29 — clickable row pills. Each
                // callback replaces (not unions) the corresponding
                // filter so a single click is a clean "narrow to just
                // this value" gesture; the operator opens the Filters
                // panel for layered multi-select queries.
                onFilterStatus={(s) => setStatusFilter([s])}
                onFilterPriority={(p) => setPriorityFilter([p])}
                onFilterLabel={(l) => setLabelFilter([l])}
              />
            ))}
          </tbody>
        </table>
      )}

      {itemsQ.data && itemsQ.data.data.length > 0 && totalPages > 1 && (
        <div
          style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}
        >
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(n) => setPageSize(n as PageSize)}
          />
        </div>
      )}

      {showCreate && hasProjects && (
        <CreateWorkItemDialog
          projects={projectsQ.data?.data ?? []}
          defaultProjectId={projectId}
          onCancel={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ["workitems"] });
            qc.invalidateQueries({ queryKey: ["stats"] });
          }}
        />
      )}
    </>
  );
}

/**
 * Tiny coloured dots that show which derived flags are active on a row.
 * Hover for a tooltip; the parent row also paints a left accent bar in
 * the same color (precedence: past-due > blocking > needs-attention).
 */
function FlagDots({
  isPastDue,
  isBlocking,
  needsAttention,
}: {
  isPastDue?: boolean;
  isBlocking?: boolean;
  needsAttention?: boolean;
}) {
  if (!isPastDue && !isBlocking && !needsAttention) return null;
  const dotStyle = (color: string): React.CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: color,
    display: "inline-block",
  });
  return (
    <span style={{ display: "inline-flex", gap: 3, marginLeft: 2 }}>
      {isPastDue && (
        <span style={dotStyle("var(--danger-primary)")} title="Past due" />
      )}
      {isBlocking && (
        <span style={dotStyle("#B86E4D")} title="Blocking other items" />
      )}
      {needsAttention && (
        <span style={dotStyle("#A56B00")} title="Needs attention" />
      )}
    </span>
  );
}

/**
 * Compact legend rendered above the table when any row in the visible
 * page has a flag — explains the colour codes without permanent clutter.
 */
function FlagLegend({ items, excludePastDue = false }: { items: WorkItem[]; excludePastDue?: boolean }) {
  // 0.1.35 — Past due moved into the Key column header. Caller passes
  // `excludePastDue` so the legend doesn't double-render the indicator.
  // Blocking + Needs attention stay here (they aren't column-scoped).
  const hasPastDue = !excludePastDue && items.some((i) => i.isPastDue);
  const hasBlocking = items.some((i) => i.isBlocking);
  const hasAttention = items.some((i) => i.needsAttention);
  if (!hasPastDue && !hasBlocking && !hasAttention) return null;
  const dot = (color: string): React.CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: color,
    display: "inline-block",
    marginRight: 4,
  });
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        fontSize: 11,
        color: "var(--text-muted)",
        padding: "4px 0 8px",
      }}
    >
      {hasPastDue && (
        <span>
          <span style={dot("var(--danger-primary)")} />
          Past due
        </span>
      )}
      {hasBlocking && (
        <span>
          <span style={dot("#B86E4D")} />
          Blocking other items
        </span>
      )}
      {hasAttention && (
        <span>
          <span style={dot("#A56B00")} />
          Needs attention
        </span>
      )}
    </div>
  );
}

function WorkItemRow({
  item,
  projectName,
  projectColor,
  cols,
  onClick,
  onAction,
  onNavigate,
  onFilterStatus,
  onFilterPriority,
  onFilterLabel,
}: {
  item: WorkItem;
  /** Resolved project name for the Key cell tooltip (ctsk-071). */
  projectName?: string;
  /** Resolved project color for the Key cell text (0.1.51 — Bug 2 fix).
   *  When undefined, the Key falls back to `var(--accent-primary)`. */
  projectColor?: string;
  cols: ColumnDef[];
  onClick: () => void;
  onAction: () => void;
  onNavigate: (p: string) => void;
  /** Row-pill click callbacks (FR 2026-05-29). When provided, the row
   *  badges render as buttons that narrow the list to that value. */
  onFilterStatus?: (status: string) => void;
  onFilterPriority?: (priority: WorkPriority) => void;
  onFilterLabel?: (label: string) => void;
}) {
  const qc = useQueryClient();

  // CTSK-237 — row-level watch toggle. Shared by the star column
  // (Part B) and the burger-menu item (Part A); both call the existing
  // idempotent POST/DELETE /workitems/:id/watch from #112 — no new
  // backend. Optimistic: the star flips instantly via a cache patch on
  // every ["workitems"] list query (the Watching page's list query
  // shares the prefix, so it stays in sync too), rolls back from the
  // onMutate snapshot on server error, and re-syncs with the server on
  // settle either way.
  const watch = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) =>
      apiSingle<{
        workItemId: string;
        isWatchedByMe: boolean;
        watcherCount: number;
      }>(`/workitems/${id}/watch`, { method: next ? "POST" : "DELETE" }),
    onMutate: async ({ id, next }) => {
      await qc.cancelQueries({ queryKey: ["workitems"] });
      const prev = qc.getQueriesData<{ data: WorkItem[] }>({
        queryKey: ["workitems"],
      });
      qc.setQueriesData<{ data: WorkItem[] }>(
        { queryKey: ["workitems"] },
        (page) =>
          page
            ? {
                ...page,
                data: page.data.map((row) =>
                  row.id === id ? { ...row, isWatchedByMe: next } : row,
                ),
              }
            : page,
      );
      return { prev };
    },
    onError: (e: Error, _vars, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
      toast.error(e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["workitems"] });
    },
  });

  const copy = useMutation({
    mutationFn: () =>
      apiSingle<WorkItem>("/workitems", {
        method: "POST",
        body: {
          projectId: item.projectId,
          title: `${item.title} (copy)`,
          description: item.description,
          priority: item.priority,
          labels: item.labels,
          parentId: item.parentId ?? null,
        },
      }),
    onSuccess: (created) => {
      toast.success(`Created ${created.key}`);
      onAction();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: () =>
      apiFetch<void>(`/workitems/${item.id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Deleted");
      onAction();
    },
  });

  function renderCell(c: ColumnDef) {
    switch (c.key) {
      case "watch": {
        // CTSK-237 — star toggle. ★ filled = watching (accent), ☆
        // empty = not (muted). Click toggles in place — the whole
        // point is not leaving the list. stopPropagation so the row's
        // own onClick doesn't ALSO navigate to the detail page.
        // Glyph literals match WorkItemDetail / DocumentLibrary /
        // Watching — no new icon library.
        const watching = item.isWatchedByMe ?? false;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              watch.mutate({ id: item.id, next: !watching });
            }}
            title={watching ? "Unwatch" : "Watch"}
            aria-label={watching ? "Unwatch" : "Watch"}
            aria-pressed={watching}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 2,
              fontSize: 16,
              color: watching
                ? "var(--accent-primary)"
                : "var(--text-muted)",
              lineHeight: 1,
            }}
          >
            {watching ? "★" : "☆"}
          </button>
        );
      }
      case "key": {
        // When an item has an externalId (importer-created), lead with
        // it as the primary identifier and demote the auto-assigned
        // `key` to a tooltip. UI-created items (no externalId) keep
        // the existing key-only render. Captured under "Align display
        // key with external_id" in FEATURE_REQUESTS.md.
        //
        // 0.1.6 styling: render the key in a color so the row identifier
        // visually leads the line.
        //
        // 0.1.51 — key color is now derived from the WORK ITEM'S OWN
        // PROJECT.color (looked up from projectsQ), not the current
        // org's `--accent-primary`. Previously every key rendered in
        // the org accent, which made "Only CTSK looks different"
        // — because CTSK was the current org — while every other
        // project's key inherited the same accent instead of its own
        // color. Falls back to --accent-primary when a project has
        // no color set.
        const display = formatWorkItemKey(item);
        const hoverTitle =
          [
            display.secondary ? `${display.secondary}` : null,
            projectName ? `Project: ${projectName}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || undefined;
        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            // ctsk-071 — hover the Key to see the full project name.
            // Extended: when externalId is present, hover also shows
            // the auto-assigned key so the operator can still find it
            // by its display key if needed.
            title={hoverTitle}
          >
            <code
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: projectColor ?? "var(--accent-primary, #2F6BFF)",
                fontWeight: 600,
              }}
            >
              {display.primary}
            </code>
            <FlagDots
              isPastDue={item.isPastDue}
              isBlocking={item.isBlocking}
              needsAttention={item.needsAttention}
            />
          </span>
        );
      }
      case "docs": {
        // Show a link icon when the work item has at least one row in
        // document_work_item_links. Count badge appears when > 1 so
        // the operator can see "this row has lots of context"
        // without opening the detail. The whole cell is wrapped in a
        // title= tooltip ("3 linked documents") so the count is
        // accessible without making the badge mandatory at count==1.
        const n = item.linkedDocumentCount ?? 0;
        if (n === 0) {
          return (
            <span
              style={{
                color: "var(--text-muted)",
                fontSize: 11,
                opacity: 0.45,
              }}
              title="No linked documents"
            >
              —
            </span>
          );
        }
        // 0.1.52 — CTSK-167 Issue B: chip now acts on click. Stops the
        // row-level onClick from firing (which would just open the
        // workitem at top-of-page) and navigates with ?tab=documents so
        // WorkItemDetail scrolls straight to the Attachments/Linked docs
        // panel. Cursor + hover feedback flag the chip as interactive.
        return (
          <span
            title={n === 1 ? "1 linked document — open" : `${n} linked documents — open`}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onNavigate(`/workitems/${item.id}?tab=documents`);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onNavigate(`/workitems/${item.id}?tab=documents`);
              }
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              color: "var(--accent-primary, #2F6BFF)",
              cursor: "pointer",
              padding: "1px 3px",
              borderRadius: 3,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "var(--surface-sunken)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "";
            }}
          >
            <Link2 size={14} aria-hidden="true" />
            {n > 1 && (
              <span style={{ fontSize: 11, fontWeight: 600 }}>{n}</span>
            )}
          </span>
        );
      }
      case "title":
        // The cell <td> already has overflow:hidden + maxWidth:320 via
        // tdStyleFor. We add an inner div with the ellipsis triplet so
        // the title clamps to one line instead of wrapping. Full text
        // is in `title=` for hover, and the optional "child of" line
        // stays below in muted text.
        return (
          <>
            <div
              title={item.title}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <strong>{item.title}</strong>
            </div>
            {item.parentId && (
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                ↳ child of an item
              </div>
            )}
          </>
        );
      case "status":
        // ctsk-072 — override the default "<variant>: <status>" tooltip
        // with "<key>: <status>" so the badge is self-describing on a
        // work-items row (the variant noun "document" was wrong here).
        // 0.1.6 — add a status-specific icon (check / play / pause /
        // ban / eye / rotate / …) so the column scans by glyph +
        // hue at a glance, not just by word. Pair with per-status
        // pill backgrounds in globals.css (.badge--<status> rules).
        return (
          <StatusBadge
            status={item.status}
            variant="document"
            icon={statusIcon(item.status)}
            label={STATUS_LABEL[item.status]}
            title={`${formatWorkItemKey(item).primary}: ${STATUS_LABEL[item.status] ?? item.status}`}
            // Click to narrow the list to just this status (replaces
            // any existing status filter). Mirrors the TagBadge
            // clickable pattern from DocumentLibrary (ctsk-077).
            onClick={onFilterStatus}
          />
        );
      case "priority":
        return (
          <PriorityPill
            priority={item.priority}
            // Click to narrow the list to just this priority.
            onClick={onFilterPriority}
          />
        );
      case "assignees":
        // Cap at 2 inline chips + "+N" so the cell stays single-line.
        // The cell's tdStyleFor adds nowrap+ellipsis; this just keeps
        // the chip layout from wrapping inside that.
        if (item.assigneeIds.length === 0) {
          return <span style={{ color: "var(--text-muted)" }}>—</span>;
        }
        return (
          <span
            style={{
              display: "inline-flex",
              gap: 4,
              flexWrap: "nowrap",
              maxWidth: "100%",
            }}
            title={
              item.assigneeIds.length > 2
                ? `${item.assigneeIds.length} assignees`
                : undefined
            }
          >
            <UserChips ids={item.assigneeIds.slice(0, 2)} />
            {item.assigneeIds.length > 2 && (
              <span
                style={{
                  color: "var(--text-muted)",
                  fontSize: 11,
                  alignSelf: "center",
                }}
              >
                +{item.assigneeIds.length - 2}
              </span>
            )}
          </span>
        );
      case "labels":
        if (item.labels.length === 0) {
          return (
            <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>
          );
        }
        return (
          <span
            style={{
              display: "inline-flex",
              gap: 4,
              flexWrap: "nowrap",
              maxWidth: "100%",
            }}
            title={item.labels.length > 2 ? item.labels.join(", ") : undefined}
          >
            {item.labels.slice(0, 2).map((l) => (
              // Click to narrow the list to just this label. Replaces
              // any existing label filter — same single-pick gesture
              // as status/priority. Falls back to a non-interactive
              // chip when no callback is wired (defensive — current
              // call site always passes onFilterLabel).
              onFilterLabel ? (
                <button
                  key={l}
                  type="button"
                  className="tag-chip tag-chip--clickable"
                  title={`Filter by label: ${l}`}
                  onClick={(e) => { e.stopPropagation(); onFilterLabel(l); }}
                >
                  {l}
                </button>
              ) : (
                <span key={l} className="tag-chip">{l}</span>
              )
            ))}
            {item.labels.length > 2 && (
              <span
                style={{
                  color: "var(--text-muted)",
                  fontSize: 11,
                  alignSelf: "center",
                }}
              >
                +{item.labels.length - 2}
              </span>
            )}
          </span>
        );
      case "dueDate":
        if (!item.dueDate) return "—";
        {
          // Days-past-due indicator (#85). Compute client-side from
          // existing isPastDue + dueDate — no schema change. The API
          // already sets isPastDue when dueDate < now && stage is
          // open, so we just render the day count alongside.
          const days = daysPastDue(item.isPastDue, item.dueDate);
          return (
            <span
              style={{
                color: item.isPastDue ? "var(--danger-primary)" : undefined,
                fontWeight: item.isPastDue ? 600 : undefined,
                display: "inline-flex",
                gap: 6,
                alignItems: "baseline",
                whiteSpace: "nowrap",
              }}
              title={
                item.isPastDue
                  ? `Past due: ${new Date(item.dueDate).toLocaleString()}`
                  : undefined
              }
            >
              <span>{new Date(item.dueDate).toLocaleDateString()}</span>
              {days > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    opacity: 0.85,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  · {formatDaysPastDueShort(days)}
                </span>
              )}
            </span>
          );
        }
      case "createdAt":
        return new Date(item.createdAt).toLocaleDateString();
      case "updatedAt": {
        // toLocaleString() includes seconds and full year — that's
        // ~22 chars which wraps inside the 130-wide column. Use a
        // compact "YYYY-MM-DD HH:MM" form (ISO date + 24h time) so the
        // cell stays single-line and hover gets the full timestamp.
        const d = new Date(item.updatedAt);
        const datePart = d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const timePart = d.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        return (
          <span title={d.toLocaleString()}>
            {datePart} {timePart}
          </span>
        );
      }
      default:
        return null;
    }
  }

  // Row tint precedence: past-due > blocking > needs-attention. Picks the
  // strongest signal so a row with multiple flags doesn't get a confusing
  // mixed background. The flag dots in the Key column show ALL of them.
  let rowTint: string | undefined;
  let rowBorderColor: string | undefined;
  if (item.isPastDue) {
    rowTint = "color-mix(in srgb, var(--danger-primary) 6%, transparent)";
    rowBorderColor = "var(--danger-primary)";
  } else if (item.isBlocking) {
    rowTint = "color-mix(in srgb, #B86E4D 6%, transparent)"; // amber-ish
    rowBorderColor = "#B86E4D";
  } else if (item.needsAttention) {
    rowTint = "color-mix(in srgb, #A56B00 6%, transparent)"; // ochre
    rowBorderColor = "#A56B00";
  }

  return (
    <tr
      style={{
        cursor: "pointer",
        background: rowTint,
        boxShadow: rowBorderColor
          ? `inset 3px 0 0 ${rowBorderColor}`
          : undefined,
      }}
      onClick={onClick}
    >
      {cols.map((c) => (
        <td key={c.key} style={tdStyleFor(c.key)}>
          {renderCell(c)}
        </td>
      ))}
      <td
        onClick={(e) => e.stopPropagation()}
        style={{ width: 28, padding: 0 }}
      >
        <RowActionsMenu
          actions={[
            {
              label: "Edit",
              onClick: () => onNavigate(`/workitems/${item.id}`),
            },
            { label: "Make a copy", onClick: () => copy.mutate() },
            {
              label: "Open in new tab",
              onClick: () => window.open(`/workitems/${item.id}`, "_blank"),
            },
            {
              // CTSK-235 — copy the short shareable URL (/wi/<key>)
              // rather than the ULID form. Human-readable, works
              // identically since /wi/:key redirects to the canonical
              // /workitems/<ULID>. Falls back to ULID form only if the
              // item somehow lacks a key (shouldn't happen post-CTSK-84).
              label: "Copy link",
              onClick: async () => {
                const url = item.key
                  ? `${window.location.origin}/wi/${item.key}`
                  : `${window.location.origin}/workitems/${item.id}`;
                const ok = await copyToClipboard(url);
                if (ok) toast.success("Link copied");
                else
                  toast.error(
                    "Could not copy — your browser blocked clipboard access",
                  );
              },
            },
            // CTSK-237 Part A — Watch / Unwatch toggle, between "Copy
            // link" and "Archive" per the ticket. Single entry, label
            // flips on current state (mirrors DocumentLibrary ctsk-051).
            // Non-destructive, so it sits above the separator that
            // fences the archive/delete danger zone.
            {
              label: item.isWatchedByMe ? "☆ Unwatch" : "★ Watch",
              onClick: () =>
                watch.mutate({ id: item.id, next: !item.isWatchedByMe }),
            },
            { label: "", onClick: () => undefined, separator: true },
            {
              label: "Archive (mark cancelled)",
              onClick: () => {
                apiSingle(`/workitems/${item.id}/transition`, {
                  method: "POST",
                  body: { to: "cancelled" },
                })
                  .then(() => {
                    toast.success("Archived");
                    onAction();
                  })
                  .catch((e: Error) => toast.error(e.message));
              },
            },
            {
              label: "Delete",
              destructive: true,
              onClick: () => {
                if (
                  confirm(
                    `Delete ${formatWorkItemKey(item).primary} — "${item.title}"?`,
                  )
                )
                  del.mutate();
              },
            },
          ]}
        />
      </td>
    </tr>
  );
}

// Quick-filter chip row above the toolbar (#99 + #112). Four controls:
//   - Assignee: All / Mine / Unassigned (segmented, client-side)
//   - Watching: All / Watching / Not watching (segmented, SERVER-side
//     via ?watchedBy — see itemsQ above)
//   - Stale: toggle pill, items with updatedAt > 14d ago (client-side)
//   - Clear all: shows when any filter (incl. priority/status/project/
//     watch) is active; resets everything in one click.
//
// Status + priority + project chips already exist in the toolbar
// below; this row is for the cross-cutting filters that don't fit
// neatly into the existing select widgets.

function QuickFilters({
  assigneeFilter,
  onAssigneeChange,
  watchFilter,
  onWatchChange,
  staleFilter,
  onStaleChange,
  staleDays,
  hasDocsFilter,
  onHasDocsChange,
  anyFilterActive,
  onClearAll,
}: {
  assigneeFilter: "all" | "mine" | "unassigned";
  onAssigneeChange: (v: "all" | "mine" | "unassigned") => void;
  watchFilter: "all" | "mine" | "not-mine";
  onWatchChange: (v: "all" | "mine" | "not-mine") => void;
  staleFilter: boolean;
  onStaleChange: (v: boolean) => void;
  staleDays: number;
  hasDocsFilter: "all" | "with" | "without";
  onHasDocsChange: (v: "all" | "with" | "without") => void;
  anyFilterActive: boolean;
  onClearAll: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        marginBottom: 8,
        fontSize: 12,
        flexWrap: "wrap",
      }}
    >
      <span style={{ color: "var(--text-muted)" }}>Quick filter:</span>

      <Segmented
        value={assigneeFilter}
        onChange={onAssigneeChange}
        options={[
          { value: "all", label: "All", glyph: "∗" },
          { value: "mine", label: "Mine", glyph: "★" },
          { value: "unassigned", label: "Unassigned", glyph: "○" },
        ]}
        label="assignee"
      />

      <Segmented
        value={watchFilter}
        onChange={onWatchChange}
        options={[
          { value: "all", label: "All", glyph: "∗" },
          { value: "mine", label: "Watching", glyph: "★" },
          { value: "not-mine", label: "Not", glyph: "☆" },
        ]}
        label="watching"
      />

      <Segmented
        value={hasDocsFilter}
        onChange={onHasDocsChange}
        options={[
          { value: "all", label: "All", glyph: "∗" },
          { value: "with", label: "With docs", glyph: "🔗" },
          { value: "without", label: "Without docs", glyph: "○" },
        ]}
        label="linked documents"
      />

      <button
        type="button"
        onClick={() => onStaleChange(!staleFilter)}
        title={`Items not updated in the last ${staleDays} days`}
        style={{
          padding: "4px 10px",
          fontSize: 12,
          borderRadius: 6,
          border: `1px solid ${staleFilter ? "var(--warning-primary, #A56B00)" : "var(--border-strong)"}`,
          background: staleFilter
            ? "color-mix(in srgb, var(--warning-primary, #A56B00) 12%, var(--surface-raised))"
            : "var(--surface-raised)",
          color: staleFilter
            ? "var(--warning-primary, #A56B00)"
            : "var(--text-primary)",
          cursor: "pointer",
          fontWeight: staleFilter ? 600 : 400,
        }}
      >
        {staleFilter ? "⏳" : "⏱"} Stale ({staleDays}d+)
      </button>

      {anyFilterActive && (
        <button
          type="button"
          onClick={onClearAll}
          style={{
            padding: "4px 8px",
            fontSize: 11,
            borderRadius: 6,
            border: "1px solid var(--border-subtle)",
            background: "transparent",
            color: "var(--text-muted)",
            cursor: "pointer",
          }}
          title="Reset every filter (project, status, priority, assignee, watching, stale)"
        >
          ✕ Clear all
        </button>
      )}
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; glyph?: string }>;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: "inline-flex",
        border: "1px solid var(--border-strong)",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              border: "none",
              background: active
                ? "var(--accent-primary)"
                : "var(--surface-raised)",
              color: active ? "#fff" : "var(--text-primary)",
              cursor: "pointer",
              fontWeight: active ? 600 : 400,
            }}
          >
            {o.glyph && (
              <span style={{ marginRight: 4, opacity: 0.7 }}>{o.glyph}</span>
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// FR 2026-05-29 — six quick-filter chips above the toolbar. Each chip
// maps to one or more underlying preset filter values; clicking a chip
// toggles into that preset (or out, when re-clicked while active). "All"
// resets just the quick-filter buckets, not explicit filters — so the
// operator can still compose a quick-filter slice with an explicit
// status / label narrow on top.
function WorkItemQuickChips({
  assigneeFilter,
  flagFilter,
  priorityFilter,
  onTogglePriority,
  hideDone,
  onToggleHideDone,
  onAll,
  onMine,
  onUnassigned,
  onOverdue,
  onBlocking,
  onAttention,
}: {
  assigneeFilter: "all" | "mine" | "unassigned";
  flagFilter: "overdue" | "blocking" | "attention" | null;
  // 0.1.35 (dr.the0 2026-06-11) — Priority chips lifted out of the
  // collapsible Filters panel into the quick-filter row so they're
  // always visible. Multi-select; clicking a chip toggles that
  // priority into / out of the filter.
  priorityFilter: WorkPriority[];
  onTogglePriority: (p: WorkPriority) => void;
  // 0.1.35 — "Hide done" toggle chip. Client-side narrow that hides
  // work items with status === 'done'. Persists with the rest of the
  // filters via URL mirror.
  hideDone: boolean;
  onToggleHideDone: () => void;
  onAll: () => void;
  onMine: () => void;
  onUnassigned: () => void;
  onOverdue: () => void;
  onBlocking: () => void;
  onAttention: () => void;
}) {
  const isAllActive = assigneeFilter === "all" && flagFilter === null;
  const chip = (
    active: boolean,
    onClick: () => void,
    label: string,
    glyph: string,
    title?: string,
  ) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      style={{
        padding: "4px 10px",
        fontSize: 12,
        borderRadius: 6,
        border: `1px solid ${active ? "var(--accent-primary)" : "var(--border-strong)"}`,
        background: active
          ? "color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))"
          : "var(--surface-raised)",
        color: active ? "var(--accent-primary-hover)" : "var(--text-primary)",
        cursor: "pointer",
        fontWeight: active ? 600 : 400,
      }}
    >
      <span style={{ marginRight: 4, opacity: 0.75 }}>{glyph}</span>
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: 8,
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--text-muted)" }}>Quick filter:</span>
      {chip(isAllActive, onAll, "All", "∗", "Clear quick-filter buckets")}
      {chip(assigneeFilter === "mine", onMine, "Mine", "👤", "Items assigned to me")}
      {chip(assigneeFilter === "unassigned", onUnassigned, "Unassigned", "○", "Items with no assignee")}
      {chip(flagFilter === "overdue", onOverdue, "Overdue", "!", "Past due date")}
      {chip(flagFilter === "blocking", onBlocking, "Blocking", "🛑", "Blocking other items")}
      {chip(flagFilter === "attention", onAttention, "Needs attention", "★", "Flagged for attention")}
      {/* 0.1.35 — Hide done toggle: filter over the page's rows that
          drops anything in a terminal workflow stage. Sits at the end
          of the existing chip set so it's visually grouped with the
          other "narrow the slice" controls.
          CTSK-180 (2026-07-24): filter broadened + tooltip corrected —
          previously said "status: done" but the predicate now covers all
          terminal states (done, verified, cancelled, deferred). Chip
          label kept as "Hide done" for muscle memory; tooltip clarifies. */}
      {chip(hideDone, onToggleHideDone, "Hide done", "⊘", "Hide items in a terminal workflow stage (done, verified, cancelled, deferred)")}
      {/* 0.1.35 — Priority chips, lifted out of the collapsible Filters
          panel. Visually offset with a thin divider + 'Priority:' label
          so the row reads as two grouped sets ("buckets" + "priorities"). */}
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 1,
          height: 18,
          background: "var(--border-subtle)",
          margin: "0 2px",
          verticalAlign: "middle",
        }}
      />
      <span style={{ color: "var(--text-muted)" }}>Priority:</span>
      {chip(priorityFilter.includes("critical"), () => onTogglePriority("critical"), "Critical", "▲", "Filter by Critical priority")}
      {chip(priorityFilter.includes("high"),     () => onTogglePriority("high"),     "High",     "△", "Filter by High priority")}
      {chip(priorityFilter.includes("medium"),   () => onTogglePriority("medium"),   "Medium",   "—", "Filter by Medium priority")}
      {chip(priorityFilter.includes("low"),      () => onTogglePriority("low"),      "Low",      "▽", "Filter by Low priority")}
    </div>
  );
}

// FR 2026-05-29 — Filters panel. Mirrors DocumentLibrary's
// FilterPanel/MultiChip pattern. 3-column grid; multi-select chips for
// enum filters; project is a single-select dropdown; date range uses
// two native date inputs.
function WorkItemFiltersPanel({
  projectId,
  onProjectIdChange,
  projects,
  statusFilter,
  onStatusFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  labelFilter,
  onLabelFilterChange,
  labelsInView,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
}: {
  projectId: string;
  onProjectIdChange: (v: string) => void;
  projects: Project[];
  statusFilter: string[];
  onStatusFilterChange: (v: string[]) => void;
  priorityFilter: WorkPriority[];
  onPriorityFilterChange: (v: WorkPriority[]) => void;
  labelFilter: string[];
  onLabelFilterChange: (v: string[]) => void;
  labelsInView: string[];
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
}) {
  const statusOptions = Object.keys(STATUS_LABEL);
  const togglePriority = (p: WorkPriority) =>
    priorityFilter.includes(p)
      ? onPriorityFilterChange(priorityFilter.filter((x) => x !== p))
      : onPriorityFilterChange([...priorityFilter, p]);
  const toggleStatus = (s: string) =>
    statusFilter.includes(s)
      ? onStatusFilterChange(statusFilter.filter((x) => x !== s))
      : onStatusFilterChange([...statusFilter, s]);
  const toggleLabel = (l: string) =>
    labelFilter.includes(l)
      ? onLabelFilterChange(labelFilter.filter((x) => x !== l))
      : onLabelFilterChange([...labelFilter, l]);

  return (
    <div
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        padding: 16,
        marginBottom: 12,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 16,
      }}
    >
      <FilterPanel label="Project">
        <select
          value={projectId}
          onChange={(e) => onProjectIdChange(e.target.value)}
          style={{ width: "100%", padding: 4 }}
        >
          <option value="">(any)</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.key} — {p.name}
            </option>
          ))}
        </select>
      </FilterPanel>
      <FilterPanel label="Status">
        <FilterChipGrid>
          {statusOptions.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter.includes(s)}
              onClick={() => toggleStatus(s)}
            >
              {STATUS_LABEL[s] ?? s}
            </FilterChip>
          ))}
        </FilterChipGrid>
      </FilterPanel>
      {/* 0.1.35 (dr.the0 2026-06-11) — Priority filter chips were lifted
          out of this panel and rendered inline in the quick-filter chip
          row above the toolbar. The Filters panel now keeps Status /
          Labels / Project / Updated-between only. */}
      <FilterPanel
        label="Labels"
        hint={labelsInView.length === 0 ? "no labels in view" : undefined}
      >
        {labelsInView.length === 0 ? (
          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
            Labels appear here once items on this page carry them.
          </span>
        ) : (
          <FilterChipGrid>
            {labelsInView.map((l) => (
              <FilterChip
                key={l}
                active={labelFilter.includes(l)}
                onClick={() => toggleLabel(l)}
              >
                {l}
              </FilterChip>
            ))}
          </FilterChipGrid>
        )}
      </FilterPanel>
      <FilterPanel label="Updated between">
        <div
          style={{
            display: "flex",
            gap: 4,
            alignItems: "center",
            fontSize: 12,
          }}
        >
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            style={{ flex: 1, padding: 4 }}
          />
          <span>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            style={{ flex: 1, padding: 4 }}
          />
        </div>
      </FilterPanel>
    </div>
  );
}

// Mini layout helpers used by WorkItemFiltersPanel — kept local to this
// file so the WorkItems list owns its filter UX without leaking shared
// styling primitives. Identical in spirit to DocumentLibrary's local
// FilterPanel + MultiChip, but light enough that the duplication is
// cheaper than a shared module.
function FilterPanel({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}
        >
          {label}
        </span>
        {hint && (
          <span
            style={{
              fontSize: 10,
              color: "var(--text-muted)",
            }}
          >
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
function FilterChipGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{children}</div>
  );
}
function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        border: `1px solid ${active ? "var(--accent-primary)" : "var(--border-strong)"}`,
        background: active
          ? "color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))"
          : "var(--surface-raised)",
        color: active ? "var(--accent-primary-hover)" : "var(--text-primary)",
        cursor: "pointer",
        textTransform: "capitalize",
      }}
    >
      {children}
    </button>
  );
}

function PriorityFilter({
  value,
  onChange,
}: {
  value: WorkPriority[];
  onChange: (v: WorkPriority[]) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 4,
        border: "1px solid var(--border-strong)",
        borderRadius: 6,
        padding: 2,
      }}
    >
      {WORK_PRIORITIES.map((p) => (
        <button
          key={p}
          className={clsx("btn", "btn--ghost")}
          style={{
            background: value.includes(p)
              ? "var(--surface-sunken)"
              : "transparent",
            border: "none",
            padding: "2px 8px",
            fontSize: 12,
            textTransform: "capitalize",
          }}
          onClick={() =>
            onChange(
              value.includes(p) ? value.filter((x) => x !== p) : [...value, p],
            )
          }
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function CreateWorkItemDialog({
  projects,
  defaultProjectId,
  onCancel,
  onCreated,
}: {
  projects: Project[];
  defaultProjectId?: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [projectId, setProjectId] = useState(
    defaultProjectId || projects[0]?.id || "",
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<WorkPriority>("medium");
  const [status, setStatus] = useState<string>("");
  const [dueDate, setDueDate] = useState<string>("");
  const [labelInput, setLabelInput] = useState<string>("");
  const [parentId, setParentId] = useState<string>("");

  const workflowsQ = useQuery({
    queryKey: ["workflows"],
    queryFn: () => apiList<WorkflowDefinition>("/workflows"),
  });

  // For the parent picker, fetch other items in the same project.
  const peersQ = useQuery({
    queryKey: ["workitems", "peers", projectId],
    queryFn: () =>
      apiList<WorkItem>("/workitems", { query: { projectId, limit: 200 } }),
    enabled: !!projectId,
  });

  const project = projects.find((p) => p.id === projectId);
  const workflow = workflowsQ.data?.data.find(
    (w) => w.id === project?.defaultWorkflowId,
  );

  // Pre-select the default status when the workflow loads.
  useEffect(() => {
    if (workflow && !status) {
      const def = workflow.states.find((s) => s.isDefault);
      if (def) setStatus(def.id);
    }
  }, [workflow, status]);

  const labels = labelInput
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const create = useMutation({
    mutationFn: () =>
      apiSingle<WorkItem>("/workitems", {
        method: "POST",
        body: {
          projectId,
          title,
          description: description || undefined,
          priority,
          status: status || undefined,
          dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
          labels,
          parentId: parentId || null,
        },
      }),
    onSuccess: () => {
      toast.success("Work item created");
      onCreated();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      role="dialog"
      aria-labelledby="new-wi-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.3)",
        display: "grid",
        placeItems: "center",
        zIndex: 2000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface-raised)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-6)",
          width: 580,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <h2 id="new-wi-title" style={{ margin: 0, marginBottom: 16 }}>
          New work item
        </h2>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <div className="field">
            <label htmlFor="wi-project">Project</label>
            <select
              id="wi-project"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setStatus("");
              }}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.key} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="wi-status">Initial state</label>
            <select
              id="wi-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">(workflow default)</option>
              {workflow?.states
                .slice()
                .sort((a, b) => a.position - b.position)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="wi-title-input">Title</label>
          <input
            id="wi-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            placeholder="A short, action-oriented title"
          />
        </div>

        <div className="field">
          <label htmlFor="wi-desc">Description (Markdown supported)</label>
          <textarea
            id="wi-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="What needs to be done? Acceptance criteria, links, references…"
            style={{
              padding: 8,
              border: "1px solid var(--border-strong)",
              borderRadius: 4,
              fontFamily: "inherit",
            }}
          />
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <div className="field">
            <label htmlFor="wi-pri">Priority</label>
            <select
              id="wi-pri"
              value={priority}
              onChange={(e) => setPriority(e.target.value as WorkPriority)}
            >
              {WORK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="wi-due">Due date</label>
            <input
              id="wi-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="wi-labels">Labels (comma-separated)</label>
          <input
            id="wi-labels"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder="frontend, urgent, refactor"
          />
        </div>

        <div className="field">
          <label htmlFor="wi-parent">
            Parent (optional — makes this a sub-item)
          </label>
          <select
            id="wi-parent"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">— None (top-level item) —</option>
            {peersQ.data?.data.map((p) => (
              <option key={p.id} value={p.id}>
                {p.key} — {p.title}
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={!projectId || !title || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create work item"}
          </button>
        </div>
      </div>
    </div>
  );
}
